import type { StartWorkflowRunRequest } from './workflowTypes'

/**
 * Pure scope/time-window helpers shared by the troubleshooting run surface.
 * Kept framework-free so the WorkflowBuilder hook imports one implementation
 * instead of duplicating it, and so the branching (CSV split/de-dup,
 * contextType-based document folding, empty→undefined collapse, timestamp
 * validation) is unit-testable without rendering.
 */

export type WorkflowContextType = 'workspace' | 'document'

export function buildWorkflowScope(input: {
  contextType: WorkflowContextType
  contextId: string
  tags: string
  equipment: string
  documentIds: string
  boardId: string
}): StartWorkflowRunRequest['input']['scope'] | undefined {
  const documentIds = [
    ...parseCsv(input.documentIds),
    ...(input.contextType === 'document' && input.contextId
      ? [input.contextId]
      : []),
  ]
  const scope = {
    ...(input.contextType === 'workspace' && input.contextId
      ? { workspaceId: input.contextId }
      : {}),
    ...(input.boardId.trim() ? { boardId: input.boardId.trim() } : {}),
    ...(documentIds.length ? { documentIds: unique(documentIds) } : {}),
    ...nonEmptyArray('tags', parseCsv(input.tags)),
    ...nonEmptyArray('equipment', parseCsv(input.equipment)),
  }
  return Object.keys(scope).length ? scope : undefined
}

export function buildTimeWindow(
  start: string,
  end: string
): StartWorkflowRunRequest['input']['timeWindow'] | undefined {
  const timeWindow = {
    ...(start.trim() ? { start: start.trim() } : {}),
    ...(end.trim() ? { end: end.trim() } : {}),
  }
  return Object.keys(timeWindow).length ? timeWindow : undefined
}

export function parseCsv(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function unique(values: string[]): string[] {
  return [...new Set(values)]
}

// Date or date-time with optional fractional seconds (any precision) and
// Z / ±hh[:]mm offset; a space is tolerated in place of the T separator.
// Date.parse alone is too permissive — it accepts free text like "June 8",
// which the setup form's error message ("must use ISO 8601") forbids.
const ISO_8601_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

/** True when a non-empty time-window field is not an ISO 8601 timestamp. */
export function isInvalidTimestamp(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed.length === 0) return false
  return !ISO_8601_RE.test(trimmed) || Number.isNaN(Date.parse(trimmed))
}

export function nonEmptyArray<Key extends string>(
  key: Key,
  value: string[]
): Record<Key, string[]> | Record<string, never> {
  return value.length ? ({ [key]: unique(value) } as Record<Key, string[]>) : {}
}
